import { Status } from 'utils/customTypes';
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  IdSelector,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from '../../store';
import { SLICE_STATUS } from '../../../utils/constants';
import {
  BaseInsightPayload,
  isDrilldownEntries,
  isDrilldownRoot,
} from 'types/store/normalized/insights/InsightDataPayload';
import { isInsightCatalogue } from 'types/store/normalized/insights/InsightCatalogue';
import { setInsightFilterMetadata } from './insightFilterMetadataSlice';

/* ============================== STATE SETUP =============================== */

const selectId: IdSelector<BaseInsightPayload> = (
  payload: BaseInsightPayload
): string => {
  return payload.data.id;
};

interface ChartDataState {
  statusByUrl: Record<string, Status>;
}

const initialState: ChartDataState = {
  statusByUrl: {},
};

const insightChartDataAdapter = createEntityAdapter<BaseInsightPayload>({
  selectId,
});

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { statusByUrl } = getState().normalized.insightChartData;
  const { dataUrl } = thunkParameters as { dataUrl: string };
  return statusByUrl[dataUrl] !== SLICE_STATUS.LOADING;
}

export const getInsightChartData = createAsyncThunk<
  BaseInsightPayload,
  { dataUrl: string; filters?: Record<string, unknown> },
  ThunkApi
>(
  'normalized/insightChartData/get',
  async ({ dataUrl, filters }, { extra: { getInsightDataApi }, dispatch }) => {
    const payload = await getInsightDataApi().getInsightData(dataUrl, filters);

    if (payload.meta?.filters && isInsightCatalogue(payload.data.id)) {
      dispatch(
        setInsightFilterMetadata({
          insightId: payload.data.id,
          filters: payload.meta.filters,
        })
      );
    }

    return payload;
  },
  { condition: concurrencyCondition }
);

/* ================================= REDUCER ================================ */
const insightChartData = createSlice({
  name: 'normalized/insightChartData',
  initialState: insightChartDataAdapter.getInitialState(initialState),
  reducers: {
    clearAllChartData: (state) => {
      insightChartDataAdapter.removeAll(state);
      state.statusByUrl = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInsightChartData.pending, (state, action) => {
        state.statusByUrl[action.meta.arg.dataUrl] = SLICE_STATUS.LOADING;
      })
      .addCase(getInsightChartData.fulfilled, (state, action) => {
        state.statusByUrl[action.meta.arg.dataUrl] = SLICE_STATUS.SUCCESS;
        insightChartDataAdapter.upsertOne(state, action.payload);
      })
      .addCase(getInsightChartData.rejected, (state, action) => {
        state.statusByUrl[action.meta.arg.dataUrl] = SLICE_STATUS.FAILED;
      });
  },
});

/* ================================ ACTIONS ================================= */
export const { clearAllChartData } = insightChartData.actions;

/* =============================== SELECTORS ================================ */
const defaultSelectors = insightChartDataAdapter.getSelectors<RootState>(
  (state) => state.normalized.insightChartData
);

const selectStatus = (
  state: RootState,
  dataUrl: string
): Status | undefined => {
  return state.normalized.insightChartData.statusByUrl[dataUrl];
};

const selectChartDataByUrl = (
  state: RootState,
  dataUrl: string
): BaseInsightPayload | undefined => {
  return state.normalized.insightChartData.entities[dataUrl];
};

const selectChartDataById = (
  state: RootState,
  id: string
): BaseInsightPayload | undefined => {
  return state.normalized.insightChartData.entities[id];
};

const selectChartDataByInsightId = (
  state: RootState,
  insightId?: string
): BaseInsightPayload | undefined => {
  return Object.values(defaultSelectors.selectEntities(state)).find(
    (chartData) => chartData?.data.id === insightId
  );
};

const allFetchedUrls = (state: RootState): Set<string> => {
  return new Set(Object.keys(state.normalized.insightChartData.statusByUrl));
};

const selectDrilldownLinkByInsightId =
  (insightId?: string, drilldownKey?: string) =>
  (state: RootState): string | undefined => {
    const chartData = selectChartDataByInsightId(state, insightId);

    if (!chartData) {
      return undefined;
    }

    const drilldown = chartData.relationships?.drilldown;

    if (!drilldownKey) {
      return isDrilldownRoot(drilldown) ? drilldown.links.related : undefined;
    }

    return isDrilldownEntries(drilldown)
      ? drilldown[drilldownKey]?.links?.related
      : undefined;
  };

export const chartDataSelectors = {
  ...defaultSelectors,
  selectStatus,
  selectChartDataByUrl,
  selectChartDataById,
  allFetchedUrls,
  selectDrilldownLinkByInsightId,
};

export default insightChartData.reducer;
